import React, { useImperativeHandle, useRef, useState } from 'react';
import { Modal, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { debounce } from 'lodash';
import intl from 'react-intl-universal';

import getRandomId from '@/utils/uuid';
import { PaymentType, PAYMENT_TYPE_MAP, PriceChannel } from '@/config';
import { renderContent, renderIntlLabel } from '@/utils';
import { parseFen2Currency } from '@/utils/number';

const autoInstructionsMap = {
  // 自动
  [PaymentType.ZFB]: renderIntlLabel('bill_desc_PaymentType_ZFB'),
  [PaymentType.WX]: renderIntlLabel('bill_desc_PaymentType_WX'),
  [PaymentType.UNION_PAY]: renderIntlLabel('bill_desc_PaymentType_UNION_PAY'),
  [PaymentType.MEMBER_CARD]: renderIntlLabel('bill_desc_PaymentType_MEMBER_CARD'),
  [PaymentType.XUANZHU]: renderIntlLabel('bill_desc_PaymentType_XUANZHU'),
  [PaymentType.CRS]: renderIntlLabel('bill_desc_PaymentType_CRS'),
};
const manualInstructionsMap = {
  // 手动
  [PaymentType.CASH]: renderIntlLabel('bill_desc_PaymentType_CASH'),
  [PaymentType.BANK_CARD]: renderIntlLabel('bill_desc_PaymentType_BANK_CARD'),
  [PriceChannel.MT]: renderIntlLabel('bill_desc_PaymentType_MT'),
  [PriceChannel.MT_ZL]: renderIntlLabel('bill_desc_PaymentType_MT_ZL'),
  [PriceChannel.XC]: renderIntlLabel('bill_desc_PaymentType_XC'),
  [PriceChannel.XC_ZL]: renderIntlLabel('bill_desc_PaymentType_XC_ZL'),
  [PriceChannel.FZ]: renderIntlLabel('bill_desc_PaymentType_FZ'),
  [PriceChannel.QNE]: renderIntlLabel('bill_desc_PaymentType_QNE'),
  [PriceChannel.DY]: renderIntlLabel('bill_desc_PaymentType_DY'),
  [PriceChannel.DY_ZL]: renderIntlLabel('bill_desc_PaymentType_DY_ZL'),
  [PriceChannel.JD]: renderIntlLabel('bill_desc_PaymentType_JD'),
  [PriceChannel.FX]: renderIntlLabel('bill_desc_PaymentType_FX'),
  [PriceChannel.ALSL]: renderIntlLabel('bill_desc_PaymentType_ALSL'),
  [PriceChannel.MTSL]: renderIntlLabel('bill_desc_PaymentType_MTSL'),
  [PriceChannel.JL]: renderIntlLabel('bill_desc_PaymentType_JL'),
};
const refundInstructionsMap = { ...autoInstructionsMap, ...manualInstructionsMap };

interface refundItem {
  id?: number | string;
  subjectName?: string;
  paymentType?: keyof typeof refundInstructionsMap;
  refundAmount?: number;
  isAcrossNight?: boolean; // 是否跨夜审退款
}

export interface IRefundRef {
  myRef?: any;
  showModal?: (values: { dataSource?: Array<refundItem>; onConfirm?: () => void }) => void;
}

const columns: ColumnsType<refundItem> = [
  { title: renderIntlLabel('order_frontDesk_label_subjectName'), dataIndex: 'subjectName' },
  {
    title: renderIntlLabel('order_frontDesk_label_paymentType'),
    dataIndex: 'paymentType',
    render: (val) => PAYMENT_TYPE_MAP[val],
  },
  {
    title: renderIntlLabel('order_frontDesk_refundAmount'),
    dataIndex: 'refundAmount',
    render: (val) => renderContent(parseFen2Currency(val)),
  },
  {
    title: renderIntlLabel('order_frontDesk_refundExplain'),
    render: (_, record) => {
      // 2023/11/23 v1.35
      if (record.paymentType === PaymentType.CRS && record.isAcrossNight) {
        return renderIntlLabel('order_frontDesk_msg_dealwith_refund');
      }
      return refundInstructionsMap[record.paymentType];
    },
  },
];

// 分类
const formatSourceData = (list: refundItem[]) => {
  // 自动退款数据
  const autoRefundList = [];
  // 手动退款数据
  const manualRefundList = [];
  (list || []).forEach((item) => {
    const { paymentType } = item;
    // CRS跨夜审归到手动退款里
    if (paymentType === PaymentType.CRS) {
      if (item.isAcrossNight) {
        manualRefundList.push(item);
      } else {
        autoRefundList.push(item);
      }
      return;
    }
    if (autoInstructionsMap[paymentType]) {
      autoRefundList.push(item);
    }
    if (manualInstructionsMap[paymentType]) {
      manualRefundList.push(item);
    }
  });
  return { autoRefundList, manualRefundList };
};

const RefundByPaymentTypeModal: React.FC<any> = ({ myRef }) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<Array<refundItem>>();
  const paramsRef = useRef<any>();

  const showModal = (values) => {
    paramsRef.current = values;
    const { dataSource: data } = values;
    setDataSource(data);
    setVisible(true);
  };

  const handleOk = debounce(() => {
    setVisible(false);
    const { onConfirm } = paramsRef.current;
    if (typeof onConfirm === 'function') {
      onConfirm();
    }
  }, 500);

  const handleCancel = () => {
    setVisible(false);
  };

  useImperativeHandle(myRef, () => ({ showModal }), []);

  const { autoRefundList, manualRefundList } = formatSourceData(dataSource);

  return (
    <>
      <Modal
        title={renderIntlLabel('order_action_confirm_of_refund')}
        width={900}
        visible={visible}
        okText={renderIntlLabel('order_action_confirm_to_refund')}
        okButtonProps={{
          danger: true,
        }}
        onCancel={handleCancel}
        onOk={handleOk}
        zIndex={1001}
      >
        {autoRefundList?.length ? (
          <div style={{ marginBottom: '32px' }}>
            <div style={{ marginBottom: '8px' }}>
              {intl.getHTML('bill_msg_auto_return_into_user_account')}
            </div>
            <Table
              rowKey={(record) => record?.id || getRandomId()}
              columns={columns}
              dataSource={autoRefundList}
              pagination={false}
            />
          </div>
        ) : null}
        {manualRefundList?.length ? (
          <div>
            <div style={{ marginBottom: '8px' }}>
              {intl.getHTML('bill_msg_manually_return_into_user')}
            </div>
            <Table
              rowKey={(record) => record?.id || getRandomId()}
              columns={columns}
              dataSource={manualRefundList}
              pagination={false}
            />
          </div>
        ) : null}
      </Modal>
    </>
  );
};

export default React.forwardRef<IRefundRef, any>((props, ref) => (
  <RefundByPaymentTypeModal {...props} myRef={ref} />
));
